import { TypeOfConsideration } from '../pricing/models/type-of-consideration.enum';
import { CorporateEquityType, DeferredVariablePaymentType } from '../loi-form/term-form';
import { LoiComparisonFormDto } from './models/loi-comparison-form.model';

// Mirrors the fairValue cell derivation in LoiComparisonRows.tsx's build*Row
// functions — keep the two in sync when purchasePriceSummary shape changes.
export const getFairValuesByType = (
    dto: LoiComparisonFormDto,
): Partial<Record<TypeOfConsideration, number | null>> => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    if (!summary) {
        return {};
    }
    const dvpRows = summary.deferredVariablePayments?.rows ?? [];
    const earnout = dvpRows.find((row) => row.type === DeferredVariablePaymentType.Earnout);
    const synthetic = dvpRows.find((row) => row.type === DeferredVariablePaymentType.Synthetic);
    const corpRows = summary.corporateEquity?.rows ?? [];
    const preferred = corpRows.find((row) => row.type === CorporateEquityType.Preferred);
    const common = corpRows.find((row) => row.type === CorporateEquityType.Common);

    return {
        [TypeOfConsideration.CashAtClose]: summary.cashAtCloser?.fairValue ?? null,
        [TypeOfConsideration.DeferredFixedPayments]:
            summary.deferredFixedPayments?.rows[0]?.fairValue ?? null,
        [TypeOfConsideration.DeferredVariablePayments]: null,
        [TypeOfConsideration.Earnout]: earnout?.fairValue ?? null,
        [TypeOfConsideration.SyntheticJvEquity]: synthetic?.fairValue ?? null,
        [TypeOfConsideration.JvEquity]: summary.jvEquity?.row?.fairValue ?? null,
        [TypeOfConsideration.CorporateEquity]: null,
        [TypeOfConsideration.PreferredEquity]: preferred?.fairValue ?? null,
        [TypeOfConsideration.CommonEquity]: common?.fairValue ?? null,
        [TypeOfConsideration.Other]: summary.otherFairValue ?? null,
    };
};

// Mirrors the faceValue cell derivation in LoiComparisonRows.tsx's build*Row
// functions — keep the two in sync when purchasePriceSummary shape changes.
export const getFaceValuesByType = (
    dto: LoiComparisonFormDto,
): Partial<Record<TypeOfConsideration, number | null>> => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    if (!summary) {
        return {};
    }
    const dvpRows = summary.deferredVariablePayments?.rows ?? [];
    const earnout = dvpRows.find((row) => row.type === DeferredVariablePaymentType.Earnout);
    const synthetic = dvpRows.find((row) => row.type === DeferredVariablePaymentType.Synthetic);
    const corpRows = summary.corporateEquity?.rows ?? [];
    const preferred = corpRows.find((row) => row.type === CorporateEquityType.Preferred);
    const common = corpRows.find((row) => row.type === CorporateEquityType.Common);

    return {
        [TypeOfConsideration.CashAtClose]: summary.cashAtCloser?.cashAtClose ?? null,
        [TypeOfConsideration.DeferredFixedPayments]:
            summary.deferredFixedPayments?.rows[0]?.amount ?? null,
        [TypeOfConsideration.DeferredVariablePayments]: null,
        [TypeOfConsideration.Earnout]: earnout?.valueAtClosing ?? null,
        [TypeOfConsideration.SyntheticJvEquity]: synthetic?.valueAtClosing ?? null,
        [TypeOfConsideration.JvEquity]: summary.jvEquity?.row?.valueAtClosing ?? null,
        [TypeOfConsideration.CorporateEquity]: null,
        [TypeOfConsideration.PreferredEquity]: preferred?.valueAtClosing ?? null,
        [TypeOfConsideration.CommonEquity]: common?.valueAtClosing ?? null,
        [TypeOfConsideration.Other]: summary.other ?? null,
    };
};
